import { listLibrary, addLibraryItemToRoom, saveSpecToLibrary } from '@/lib/data/material-library';
import { findLikelyDuplicate } from '@/lib/material-library';
import { openLibraryPicker } from '@/views/library-picker';
import { listChangeOrders, CHANGE_ORDER_STATUS_LABEL } from '@/lib/data/change-orders';
import { roomChangeOrders, changeOrderTotal } from '@/lib/change-order-budget';
import { signedMoney } from '@/lib/change-order-ui';
import { getRoom, updateRoom, recordRoomApproval } from '@/lib/data/rooms';
import { getProject } from '@/lib/data/projects';
import { resolveMediaSrc, uploadProjectMedia, removeMedia } from '@/lib/storage';
import { listSpecs, createSpec, updateSpec, deleteSpec } from '@/lib/data/specs';
import { listPunch, updatePunch } from '@/lib/data/punch';
import { openPunchModal } from '@/lib/punch-modal';
import { listSchedule } from '@/lib/data/schedule';
import { openScheduleModal } from '@/lib/schedule-modal';
import { listRoomImages, addRoomImage, deleteRoomImage } from '@/lib/data/room-images';
import { listRevisions } from '@/lib/data/spec-revisions';
import { listRoomPalette, createPaletteColor, updatePaletteColor, deletePaletteColor } from '@/lib/data/palette';
import { esc, fmtDate, fmtMoney, setPageTitle } from '@/lib/dom';
import { openModal } from '@/lib/modal';
import { openEditRoomModal, roomListHash } from '@/lib/room-edit';
import { toastError, toastSuccess } from '@/lib/toast';
import {
  ROOM_STATUS_LABEL, SPEC_STATUS_LABEL,
  SCHEDULE_STATUS_LABEL, asOptions,
} from '@/lib/labels';
import { renderFilterChips, bindFilterChips } from '@/lib/filter-chips';
import { getShell } from '@/lib/shell-handle';
import { isSpecOverdue } from '@/lib/spec-status';
import {
  approvalDetail, completionStamp, withdrawalWarning, roomStatusActions,
  roomReadiness, completionWarning, readinessMarkup,
} from '@/lib/room-status';
import { listMaterialCategories, createMaterialCategory } from '@/lib/data/material-categories';
import { indexMaterialCategories, materialCategoryOptions, type MaterialCategoryIndex } from '@/lib/material-categories';
import { listRoomFeedback, resolveRoomFeedback } from '@/lib/data/room-feedback';
import { listContacts } from '@/lib/data/contacts';
import { forgetProject } from '@/lib/data/project-workspace';
import { punchVendorName } from '@/lib/punch';
import type {
  RoomRow, ProjectRow, SpecRow, PunchItemRow, ScheduleEntryRow,
  RoomImageRow, RoomImageKind, SpecRevisionRow, PaletteColorRow, RoomFeedbackRow,
  MaterialCategoryRow, ChangeOrderRow, DesignerContactRow,
} from '@/types/database';
import { emptyToNull } from '@/lib/form-values';

let specStatusFilter: SpecRow['status'] | 'all' = 'all';

// Order matters — this drives the on-screen section order under "Room images".
export async function renderRoomDetail(root: HTMLElement, roomId: string): Promise<void> {
  root.innerHTML = `<section class="page"><div class="loading">Loading room…</div></section>`;
  try {
    const room = await getRoom(roomId);
    if (!room) {
      root.innerHTML = `<section class="page"><a class="back-link" href="#/projects">← All projects</a><div class="empty"><p>Room not found.</p></div></section>`;
      return;
    }
    const [project, specs, punch, schedule, images, palette, feedback, categories, changeOrders, contacts] = await Promise.all([
      getProject(room.project_id),
      listSpecs(roomId),
      listPunch(roomId),
      listSchedule(roomId),
      listRoomImages(roomId),
      listRoomPalette(roomId),
      listRoomFeedback(roomId).catch(() => [] as RoomFeedbackRow[]),
      listMaterialCategories().catch(() => [] as MaterialCategoryRow[]),
      listChangeOrders(room.project_id).catch(() => [] as ChangeOrderRow[]),
      // For the punch list's vendor picker. A failure here costs a dropdown,
      // not the page.
      listContacts(room.project_id).catch(() => [] as DesignerContactRow[]),
    ]);
    setPageTitle(project ? `${room.name} · ${project.name}` : room.name);
    if (project) {
      getShell()?.setContext({
        kind: 'project', projectId: project.id, projectName: project.name,
        activeSection: 'rooms', activeRoomName: room.name,
      });
    }
    paint(root, room, project, specs, punch, schedule, images, palette, feedback, categories, changeOrders, contacts);
  } catch (err) {
    root.innerHTML = `<section class="page"><a class="back-link" href="#/projects">← All projects</a><div class="auth-error">${esc(err instanceof Error ? err.message : 'Failed to load')}</div></section>`;
  }
}

function paint(
  root: HTMLElement,
  room: RoomRow,
  project: ProjectRow | null,
  specs: SpecRow[],
  punch: PunchItemRow[],
  schedule: ScheduleEntryRow[],
  images: RoomImageRow[],
  palette: PaletteColorRow[],
  feedback: RoomFeedbackRow[],
  categories: MaterialCategoryRow[],
  changeOrders: ChangeOrderRow[],
  contacts: DesignerContactRow[],
): void {
  const cats = indexMaterialCategories(categories);
  const reload = (): void => {
    // Everything on this page belongs to the project behind it — its rooms,
    // specs, punch items, schedule. The project page caches that (0044-era
    // performance work), so a save here has to drop the cache or going back
    // shows the room as it was.
    forgetProject(room.project_id);
    void renderRoomDetail(root, room.id);
  };
  // The lists are already scoped to this room — see roomReadiness().
  const readiness = roomReadiness(specs, punch, schedule);

  root.innerHTML = `
    <section class="page room-detail">
      <header class="hero ${room.hero_image_url ? 'hero-with-image' : ''}">
        ${room.hero_image_url ? `<div class="hero-image"><img data-media-src="${esc(room.hero_image_url)}" alt="" /></div>` : ''}
        <div class="hero-info">
          <div class="eyebrow">${room.is_exterior ? 'Exterior' : 'Interior'}</div>
          <h1>${esc(room.name)}</h1>
          <div class="hero-meta">
            <div><span class="muted">Status</span><div>
              <span class="chip chip-${esc(room.status)}">${esc(ROOM_STATUS_LABEL[room.status])}</span>
              ${completionStamp(room) ? `<div class="muted approved-stamp">${esc(completionStamp(room)!)}</div>` : ''}
              ${approvalDetail(room) ? `<div class="muted approved-stamp">${esc(approvalDetail(room)!)}</div>` : ''}
              <div class="room-status-actions">${roomStatusActions(room.status).map((a) =>
                // .btn-status-toggle is a text-link style (zero side padding),
                // written for the old single toggle. Kept off the primary so
                // "Mark completed" reads as a button rather than a cramped link.
                `<button class="${a.primary ? 'btn-edit-project' : 'btn-link btn-status-toggle'}"
                         data-room-status-to="${esc(a.to)}">${esc(a.label)}</button>`).join('')}</div>
              ${room.status === 'approved' ? readinessMarkup(readiness) : ''}
            </div></div>
            <div><span class="muted">Materials</span><div>${specs.length}</div></div>
            <div><span class="muted">Punch open</span><div>${punch.filter(p => !p.complete).length}</div></div>
          </div>
          ${room.notes ? `<p class="hero-desc">${esc(room.notes)}</p>` : ''}
          <div class="hero-actions">
            <button class="btn-edit-project" data-action="edit-room">Edit room</button>
            <button class="btn-link" data-action="room-hero-image">${room.hero_image_url ? 'Change' : 'Add'} hero image</button>
            ${room.hero_image_url ? `<button class="btn-link btn-danger-link" data-action="room-hero-image-remove">Remove image</button>` : ''}
          </div>
        </div>
      </header>

      <nav class="tab-bar" data-room-tabs>
        <button class="tab" data-tab-target="overview">Overview</button>
        <button class="tab" data-tab-target="mood">Inspiration</button>
        <button class="tab" data-tab-target="ba">Before &amp; After</button>
        <button class="tab" data-tab-target="palette">Paint palette</button>
        <button class="tab" data-tab-target="punch">Punch list</button>
        <button class="tab" data-tab-target="schedule">Install schedule</button>
      </nav>

      <div class="tab-panel" data-tab="overview">
        ${(() => {
          const open = feedback.filter((f) => !f.resolved_at);
          if (open.length === 0) return '';
          return `<section class="panel">
            <div class="section-bar"><div class="section-bar-left"><h3>Client requests</h3></div></div>
            <ul class="room-feedback">
              ${open.map((f) => `
                <li>
                  <div class="room-feedback-body">${esc(f.body)}</div>
                  <div class="room-feedback-foot">
                    <span class="muted">${esc(f.author_name)} · ${esc(fmtDate(f.created_at))}</span>
                    <button class="btn-link" data-resolve-feedback="${esc(f.id)}">Mark handled</button>
                  </div>
                </li>`).join('')}
            </ul>
          </section>`;
        })()}
        ${renderRoomChangeOrders(room, changeOrders)}
        <section class="panel">
          ${(() => {
            if (specs.length === 0) {
              return `<div class="section-bar"><div class="section-bar-left"><h3>Materials</h3></div><span class="section-bar-actions"><button class="btn-ghost" data-action="add-from-library">From library</button><button class="btn-ghost" data-action="add-spec">+ Add material</button></span></div>
                <div class="empty-inline">No materials yet.</div>`;
            }
            const counts = new Map<string, number>();
            specs.forEach((s) => counts.set(s.status, (counts.get(s.status) ?? 0) + 1));
            const filterHtml = `<div data-spec-filter>${renderFilterChips({
              values: (Object.keys(SPEC_STATUS_LABEL) as SpecRow['status'][])
                .filter((k) => counts.has(k))
                .map((k) => ({ value: k, label: SPEC_STATUS_LABEL[k], count: counts.get(k) ?? 0 })),
              active: specStatusFilter,
            })}</div>`;
            const visible = specStatusFilter === 'all'
              ? specs : specs.filter((s) => s.status === specStatusFilter);
            return `<div class="section-bar"><div class="section-bar-left"><h3>Materials</h3>${filterHtml}</div><span class="section-bar-actions"><button class="btn-ghost" data-action="add-from-library">From library</button><button class="btn-ghost" data-action="add-spec">+ Add material</button></span></div>
              <div class="table-wrap"><table class="specs-table">
                <thead><tr>
                  <th></th><th>Item</th><th>Vendor</th><th>Category</th><th>Qty</th><th>Cost</th>
                  <th>Status</th><th>Est. delivery</th><th></th>
                </tr></thead>
                <tbody>${visible.map((s) => renderSpecRow(s, cats)).join('')}</tbody>
              </table></div>`;
          })()}
        </section>
      </div>

      <div class="tab-panel" data-tab="mood">
        <section class="panel room-image-gallery">
          <div class="section-bar"><div class="section-bar-left"><h3>Inspiration</h3></div></div>
          ${renderGallerySection('inspiration', 'Inspiration', images.filter((i) => i.kind === 'inspiration'))}
        </section>
        <section class="panel room-image-gallery">
          <div class="section-bar"><div class="section-bar-left"><h3>Details</h3></div></div>
          ${renderGallerySection('detail', 'Details', images.filter((i) => i.kind === 'detail'))}
        </section>
      </div>

      <div class="tab-panel" data-tab="ba">
        <section class="panel room-image-gallery">
          <div class="section-bar"><div class="section-bar-left"><h3>Before &amp; After</h3></div></div>
          ${renderBeforeAfterPairs(images)}
          ${renderGallerySection('before', 'Before', images.filter((i) => i.kind === 'before'))}
          ${renderGallerySection('after', 'After', images.filter((i) => i.kind === 'after'))}
          ${renderGallerySection('in_progress', 'In progress', images.filter((i) => i.kind === 'in_progress'))}
        </section>
      </div>

      <div class="tab-panel" data-tab="palette">
        <section class="panel">
          <div class="section-bar"><div class="section-bar-left"><h3>Paint palette</h3></div><button class="btn-ghost" data-action="add-room-color">+ Add color</button></div>
          ${palette.length === 0
            ? `<div class="empty-inline">No colors selected for this room yet.</div>`
            : `<div class="palette-grid">${palette.map(renderRoomColor).join('')}</div>`}
        </section>
      </div>

      <div class="tab-panel" data-tab="punch">
        <section class="panel">
          <div class="section-bar"><div class="section-bar-left"><h3>Punch list</h3></div><button class="btn-ghost" data-action="add-punch">+ Add</button></div>
          ${punch.length === 0
            ? `<div class="empty-inline">No punch items yet.</div>`
            : `<ul class="punch-list">${punch.map((it) => renderPunch(it, contacts)).join('')}</ul>`}
        </section>
      </div>

      <div class="tab-panel" data-tab="schedule">
        <section class="panel">
          <div class="section-bar"><div class="section-bar-left"><h3>Install schedule</h3></div><button class="btn-ghost" data-action="add-schedule">+ Add</button></div>
          ${schedule.length === 0
            ? `<div class="empty-inline">No scheduled tasks.</div>`
            : `<ul class="schedule-list">${schedule.map(renderSchedule).join('')}</ul>`}
        </section>
      </div>
    </section>
  `;

  // ── Tab switching ────────────────────────────────────────────────────────
  const tabBar = root.querySelector<HTMLElement>('[data-room-tabs]');
  const panels = root.querySelectorAll<HTMLElement>('.tab-panel[data-tab]');
  const setTab = (key: string): void => {
    const target = (key && tabBar?.querySelector(`[data-tab-target="${key}"]`)) ? key : 'overview';
    tabBar?.querySelectorAll<HTMLButtonElement>('.tab').forEach((b) => {
      b.classList.toggle('is-active', b.dataset.tabTarget === target);
    });
    panels.forEach((p) => { p.hidden = p.dataset.tab !== target; });
  };
  const initialTab = (/[?&]tab=([^&]+)/.exec(location.hash)?.[1]) ?? 'overview';
  setTab(initialTab);
  tabBar?.querySelectorAll<HTMLButtonElement>('.tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      const key = btn.dataset.tabTarget!;
      setTab(key);
      // Update hash without firing route reload.
      const base = location.hash.split('?')[0];
      history.replaceState(null, '', `${base}?tab=${encodeURIComponent(key)}`);
    });
  });

  // Resolve signed URLs for any storage-path images.
  root.querySelectorAll<HTMLImageElement>('img[data-media-src]').forEach((img) => {
    const src = img.getAttribute('data-media-src')!;
    void resolveMediaSrc(src).then((url) => { if (url) img.src = url; });
  });

  // ── Wire actions ─────────────────────────────────────────────────────────
  root.querySelectorAll('[data-action="edit-room"]').forEach((el) => {
    el.addEventListener('click', () => {
      openEditRoomModal(room, {
        onSaved: reload,
        // The room is gone, so there's nothing here to reload — go back to the
        // list it lived on.
        onDeleted: () => { location.hash = roomListHash(room); },
      });
    });
  });

  root.querySelector('[data-action="room-hero-image"]')!.addEventListener('click', () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const { path } = await uploadProjectMedia(file, room.project_id, `rooms/${room.id}`);
        if (room.hero_image_url && !/^https?:/i.test(room.hero_image_url)) {
          await removeMedia(room.hero_image_url).catch(() => {});
        }
        await updateRoom(room.id, { hero_image_url: path });
        reload();
      } catch (err) { toastError(err instanceof Error ? err.message : 'Upload failed'); }
    });
    input.click();
  });

  root.querySelector('[data-action="room-hero-image-remove"]')?.addEventListener('click', async () => {
    if (!confirm('Remove the hero image?')) return;
    if (room.hero_image_url && !/^https?:/i.test(room.hero_image_url)) {
      await removeMedia(room.hero_image_url).catch(() => {});
    }
    await updateRoom(room.id, { hero_image_url: null });
    reload();
  });

  root.querySelectorAll<HTMLButtonElement>('[data-action="upload-image"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const kind = btn.dataset.kind as RoomImageKind;
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;
      input.addEventListener('change', async () => {
        const files = Array.from(input.files ?? []);
        if (files.length === 0) return;
        try {
          const caption = files.length === 1 ? prompt('Optional caption?') ?? null : null;
          for (const file of files) {
            await addRoomImage(file, room.project_id, room.id, kind, caption);
          }
          reload();
        } catch (err) { toastError(err instanceof Error ? err.message : 'Upload failed'); }
      });
      input.click();
    });
  });

  root.querySelectorAll<HTMLButtonElement>('[data-delete-image]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.deleteImage!;
      const img = images.find((x) => x.id === id);
      if (!img) return;
      if (!confirm('Remove this image?')) return;
      try {
        await deleteRoomImage(img);
        reload();
      } catch (err) { toastError(err instanceof Error ? err.message : 'Delete failed'); }
    });
  });

  root.querySelectorAll<HTMLButtonElement>('[data-room-status-to]').forEach((el) => {
    el.addEventListener('click', async () => {
      const to = el.dataset.roomStatusTo as RoomRow['status'];

      // A FRESH approval has to say who gave it and how — the same record a
      // change order keeps (0038), and the same record the database now
      // requires (0040). Reopening a completed room is not a new approval: it
      // keeps the one it already had, so it asks nothing.
      if (to === 'approved' && room.status === 'proposed') {
        openModal({
          title: `Mark ${room.name} approved`,
          submitLabel: 'Mark approved',
          fields: [
            { name: 'approved_by', label: 'Approved by', required: true, defaultValue: project?.client_name ?? '' },
            {
              name: 'note', label: 'How it was approved (e.g. “By email, 9/19”)',
              type: 'textarea', voice: true, required: true,
            },
          ],
          onSubmit: async (v) => {
            await recordRoomApproval(room.id, String(v.approved_by ?? ''), String(v.note ?? ''));
            toastSuccess('Approval recorded');
            reload();
          },
        });
        return;
      }

      // Withdrawing: warn when there is an approval record to destroy.
      // Completing: nudge when work is still outstanding. A question, never
      // a refusal (chosen 2026-09-18).
      const warning = to === 'proposed' ? withdrawalWarning(room)
        : to === 'completed' ? completionWarning(readiness)
        : null;
      if (warning && !confirm(to === 'proposed' ? `${warning}\n\nContinue?` : warning)) return;
      try {
        await updateRoom(room.id, { status: to });
        reload();
      } catch (err) {
        toastError(err instanceof Error ? err.message : 'Could not change status');
      }
    });
  });

  // querySelectorAll — one button per open request.
  root.querySelectorAll<HTMLButtonElement>('[data-resolve-feedback]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      try {
        await resolveRoomFeedback(btn.dataset.resolveFeedback!);
        reload();
      } catch (err) {
        toastError(err instanceof Error ? err.message : 'Could not mark handled');
      }
    });
  });

  root.querySelectorAll('[data-action="add-spec"]').forEach((el) => {
    el.addEventListener('click', () => {
      openSpecModal({ projectId: room.project_id, roomId: room.id, sortOrder: specs.length, categories, onDone: reload });
    });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-edit-spec]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const s = specs.find((x) => x.id === btn.dataset.editSpec)!;
      openSpecModal({ projectId: room.project_id, roomId: room.id, sortOrder: s.sort_order, spec: s, categories, onDone: reload });
    });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-spec-history]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const s = specs.find((x) => x.id === btn.dataset.specHistory);
      if (!s) return;
      try {
        const revs = await listRevisions(s.id);
        openHistoryModal(s, revs);
      } catch (err) { toastError(err instanceof Error ? err.message : 'Failed to load history'); }
    });
  });
  // ── Material library (0039) ──
  root.querySelectorAll<HTMLButtonElement>('[data-action="add-from-library"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      try {
        const items = await listLibrary();
        if (items.length === 0) {
          toastError('Your library is empty — add products under Material Library, or use 📚 on a material to save it there.');
          return;
        }
        openLibraryPicker({
          items,
          roomName: room.name,
          onPick: async (item, quantity) => {
            const added = await addLibraryItemToRoom(item, room, quantity, specs.length);
            toastSuccess(added.photoCopied
              ? `${item.item} added to ${room.name}`
              : `${item.item} added — its photo couldn’t be copied; add one on the material`);
            reload();
          },
        });
      } catch (err) {
        toastError(err instanceof Error ? err.message : 'Could not open the library');
      } finally {
        btn.disabled = false;
      }
    });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-save-to-library]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const spec = specs.find((x) => x.id === btn.dataset.saveToLibrary);
      if (!spec) return;
      btn.disabled = true;
      try {
        const dup = findLikelyDuplicate(await listLibrary(), spec);
        if (dup && !confirm(`The library already has “${dup.item}”${dup.vendor ? ` from ${dup.vendor}` : ''}. Save another copy anyway?`)) return;
        await saveSpecToLibrary(spec);
        toastSuccess(`${spec.item} saved to the library — available in every project`);
      } catch (err) {
        toastError(err instanceof Error ? err.message : 'Could not save it to the library');
      } finally {
        btn.disabled = false;
      }
    });
  });

  root.querySelectorAll<HTMLButtonElement>('[data-upload-thumb]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const s = specs.find((x) => x.id === btn.dataset.uploadThumb);
      if (!s) return;
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.addEventListener('change', async () => {
        const file = input.files?.[0];
        if (!file) return;
        try {
          const { path } = await uploadProjectMedia(file, room.project_id, `rooms/${room.id}`);
          await updateSpec(s.id, { thumbnail_url: path });
          reload();
        } catch (err) { toastError(err instanceof Error ? err.message : 'Upload failed'); }
      });
      input.click();
    });
  });
  const specFilterEl = root.querySelector('[data-spec-filter]');
  if (specFilterEl) {
    bindFilterChips<SpecRow['status']>(specFilterEl, (val) => {
      specStatusFilter = val;
      reload();
    });
  }

  root.querySelector('[data-action="add-punch"]')!.addEventListener('click', () => {
    openPunchModal({ roomId: room.id, sortOrder: punch.length, contacts, onDone: reload });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-edit-punch]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const p = punch.find((x) => x.id === btn.dataset.editPunch)!;
      openPunchModal({ roomId: room.id, sortOrder: p.sort_order, punch: p, contacts, onDone: reload });
    });
  });
  root.querySelectorAll<HTMLInputElement>('[data-toggle-punch]').forEach((cb) => {
    cb.addEventListener('change', async () => {
      await updatePunch(cb.dataset.togglePunch!, { complete: cb.checked });
      reload();
    });
  });

  root.querySelector('[data-action="add-schedule"]')!.addEventListener('click', () => {
    openScheduleModal({ roomId: room.id, sortOrder: schedule.length, onDone: reload });
  });

  root.querySelector('[data-action="add-room-color"]')!.addEventListener('click', () => {
    openRoomColorModal({ projectId: room.project_id, roomId: room.id, sortOrder: palette.length, onDone: reload });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-edit-room-color]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const c = palette.find((x) => x.id === btn.dataset.editRoomColor)!;
      openRoomColorModal({ projectId: room.project_id, roomId: room.id, sortOrder: c.sort_order, color: c, onDone: reload });
    });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-edit-schedule]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const s = schedule.find((x) => x.id === btn.dataset.editSchedule)!;
      openScheduleModal({ roomId: room.id, sortOrder: s.sort_order, entry: s, onDone: reload });
    });
  });
}

function renderGallerySection(kind: RoomImageKind, label: string, items: RoomImageRow[]): string {
  return `
    <div class="gallery-section">
      <div class="gallery-section-head">
        <h3>${esc(label)}</h3>
        <button class="btn-link" data-action="upload-image" data-kind="${esc(kind)}">+ Upload</button>
      </div>
      ${items.length === 0
        ? `<div class="gallery-empty">No ${esc(label.toLowerCase())} images yet.</div>`
        : `<div class="room-image-grid">${items.map(renderRoomImageThumb).join('')}</div>`}
    </div>
  `;
}

function renderRoomImageThumb(img: RoomImageRow): string {
  return `
    <figure class="room-image-thumb">
      <img data-media-src="${esc(img.storage_path)}" alt="${esc(img.caption ?? '')}" />
      <button class="icon-btn room-image-delete" data-delete-image="${esc(img.id)}" title="Remove">×</button>
      ${img.caption ? `<figcaption>${esc(img.caption)}</figcaption>` : ''}
    </figure>
  `;
}

function renderBeforeAfterPairs(images: RoomImageRow[]): string {
  // Zip "before" + "after" entries by sort_order so designers can intentionally
  // pair specific shots. Anything unpaired falls into the kind sections below.
  const before = images.filter((i) => i.kind === 'before').sort((a, b) => a.sort_order - b.sort_order);
  const after  = images.filter((i) => i.kind === 'after').sort((a, b) => a.sort_order - b.sort_order);
  if (before.length === 0 || after.length === 0) return '';
  const pairs = Math.min(before.length, after.length);
  const rows: string[] = [];
  for (let i = 0; i < pairs; i++) {
    rows.push(`
      <div class="before-after-pair">
        <figure>
          <span class="ba-label">Before</span>
          <img data-media-src="${esc(before[i]!.storage_path)}" alt="" />
        </figure>
        <figure>
          <span class="ba-label">After</span>
          <img data-media-src="${esc(after[i]!.storage_path)}" alt="" />
        </figure>
      </div>
    `);
  }
  return `<div class="before-after-pairs">${rows.join('')}</div>`;
}

function renderSpecRow(s: SpecRow, cats: MaterialCategoryIndex): string {
  const total = s.unit_cost_cents == null ? null : Math.round(s.unit_cost_cents * Number(s.quantity));
  const overdue = isSpecOverdue(s);
  return `
    <tr>
      <td class="spec-thumb-cell">
        ${s.thumbnail_url
          ? `<img class="spec-thumb" data-media-src="${esc(s.thumbnail_url)}" alt="" />`
          : `<button class="spec-thumb spec-thumb-empty" data-upload-thumb="${esc(s.id)}" title="Upload thumbnail">📷</button>`}
      </td>
      <td>
        <div class="spec-item">${esc(s.item)}${s.source_url ? ` <a href="${esc(s.source_url)}" target="_blank" rel="noopener" class="spec-source" title="Open source">↗</a>` : ''}</div>
        ${s.notes ? `<div class="muted spec-notes">${esc(s.notes)}</div>` : ''}
      </td>
      <td>${esc(s.vendor ?? '—')}</td>
      <td>${cats.name(s.material_category_id) ? esc(cats.name(s.material_category_id)) : '<span class="muted">—</span>'}</td>
      <td>${esc(String(s.quantity))}${s.unit ? ' ' + esc(s.unit) : ''}</td>
      <td>${fmtMoney(total)}</td>
      <td><span class="chip chip-${esc(s.status)}">${esc(SPEC_STATUS_LABEL[s.status])}</span></td>
      <td>${fmtDate(s.estimated_delivery)}${overdue ? ` <span class="chip chip-danger">Overdue</span>` : ''}</td>
      <td>
        ${s.library_item_id ? '' : `<button class="icon-btn" data-save-to-library="${esc(s.id)}" title="Save to the firm’s material library">📚</button>`}
        <button class="icon-btn" data-spec-history="${esc(s.id)}" title="History">⏱</button>
        <button class="icon-btn" data-edit-spec="${esc(s.id)}" title="Edit">✎</button>
      </td>
    </tr>
  `;
}

function renderPunch(p: PunchItemRow, contacts: DesignerContactRow[]): string {
  const vendor = punchVendorName(p, contacts);
  return `
    <li class="punch-item ${p.complete ? 'done' : ''}">
      <input type="checkbox" data-toggle-punch="${esc(p.id)}" ${p.complete ? 'checked' : ''} />
      <div class="punch-body">
        <div>${esc(p.description)}</div>
        <div class="muted small">${vendor ? esc(vendor) + ' · ' : ''}${fmtDate(p.due_date)}</div>
      </div>
      <button class="icon-btn" data-edit-punch="${esc(p.id)}" title="Edit">✎</button>
    </li>
  `;
}

function renderSchedule(s: ScheduleEntryRow): string {
  return `
    <li class="schedule-item">
      <div>
        <div>${esc(s.task)}</div>
        <div class="muted small">${fmtDate(s.start_date)} → ${fmtDate(s.end_date)}</div>
      </div>
      <span class="chip chip-${esc(s.status)}">${esc(SCHEDULE_STATUS_LABEL[s.status])}</span>
      <button class="icon-btn" data-edit-schedule="${esc(s.id)}" title="Edit">✎</button>
    </li>
  `;
}

// ─── Modals ────────────────────────────────────────────────────────────────

const NEW_CATEGORY = '__new_category__';

function openSpecModal(opts: {
  projectId: string; roomId: string; sortOrder: number;
  spec?: SpecRow; categories: MaterialCategoryRow[]; onDone: () => void;
}): void {
  const s = opts.spec;
  openModal({
    title: s ? 'Edit material' : 'Add material',
    showDelete: !!s,
    fields: [
      { name: 'item', label: 'Item', required: true, defaultValue: s?.item },
      { name: 'vendor', label: 'Vendor', defaultValue: s?.vendor },
      { name: 'sku', label: 'SKU', defaultValue: s?.sku },
      { name: 'quantity', label: 'Quantity', type: 'number', defaultValue: s?.quantity ?? 1 },
      { name: 'unit', label: 'Unit', defaultValue: s?.unit, placeholder: 'ea, panel, slab…' },
      { name: 'unit_cost_dollars', label: 'Unit cost (USD)', type: 'number', defaultValue: s?.unit_cost_cents == null ? null : Math.round(s.unit_cost_cents / 100) },
      {
        name: 'material_category_id', label: 'Category', type: 'select',
        options: [
          { value: '', label: '— None —' },
          ...materialCategoryOptions(opts.categories),
          { value: NEW_CATEGORY, label: '+ Add a new category…' },
        ],
        defaultValue: s?.material_category_id ?? '',
      },
      { name: 'status', label: 'Status', type: 'select', options: asOptions(SPEC_STATUS_LABEL), defaultValue: s?.status ?? 'proposed' },
      { name: 'estimated_delivery', label: 'Est. delivery', type: 'date', defaultValue: s?.estimated_delivery },
      { name: 'source_url', label: 'Source URL', defaultValue: s?.source_url, placeholder: 'https://…' },
      { name: 'notes', label: 'Notes', type: 'textarea', voice: true, defaultValue: s?.notes },
    ],
    onSubmit: async (v) => {
      const payload = {
        item: String(v.item),
        vendor: emptyToNull(v.vendor),
        sku: emptyToNull(v.sku),
        quantity: Number(v.quantity) || 1,
        unit: emptyToNull(v.unit),
        unit_cost_cents: v.unit_cost_dollars ? Math.round(Number(v.unit_cost_dollars) * 100) : null,
        status: v.status as SpecRow['status'],
        // Picking "+ Add a new category…" creates it on the designer's own
        // list, so it persists and is available in every project.
        material_category_id: await (async () => {
          const val = String(v.material_category_id ?? '');
          if (val !== NEW_CATEGORY) return val || null;
          const name = prompt('Name for the new material category:');
          if (!name || !name.trim()) return null;
          return (await createMaterialCategory(name.trim())).id;
        })(),
        estimated_delivery: emptyToNull(v.estimated_delivery),
        source_url: emptyToNull(v.source_url),
        notes: emptyToNull(v.notes),
      };
      if (s) await updateSpec(s.id, payload);
      else await createSpec({ room_id: opts.roomId, sort_order: opts.sortOrder, ...payload });
      opts.onDone();
    },
    onDelete: s ? async () => { await deleteSpec(s.id); opts.onDone(); } : undefined,
  });
}

function openHistoryModal(spec: SpecRow, revs: SpecRevisionRow[]): void {
  // Build a chronological list (oldest first) of snapshots so we can diff each
  // step against the next one. The current spec row is the implicit newest.
  const chrono = [...revs].reverse();
  const states: { label: string; data: Record<string, unknown> }[] = chrono.map((r) => ({
    label: new Date(r.created_at).toLocaleString(),
    data: r.snapshot as Record<string, unknown>,
  }));
  states.push({ label: 'Current', data: spec as unknown as Record<string, unknown> });

  const TRACK = ['item','vendor','sku','quantity','unit','unit_cost_cents','status','estimated_delivery','source_url','thumbnail_url','notes'];
  const fmtVal = (v: unknown): string => v == null || v === '' ? '—' : String(v);
  const diffs: string[] = [];
  for (let i = states.length - 1; i > 0; i--) {
    const cur = states[i]!;
    const prev = states[i - 1]!;
    const changes = TRACK.filter((k) => fmtVal(cur.data[k]) !== fmtVal(prev.data[k]));
    if (changes.length === 0) continue;
    diffs.push(`
      <div class="history-entry">
        <div class="history-time">${esc(cur.label)}</div>
        <ul class="history-changes">
          ${changes.map((k) => `<li><strong>${esc(k)}</strong>: <s>${esc(fmtVal(prev.data[k]))}</s> → ${esc(fmtVal(cur.data[k]))}</li>`).join('')}
        </ul>
      </div>`);
  }

  const overlay = document.createElement('div');
  overlay.className = 'modal';
  overlay.innerHTML = `
    <div class="modal-card">
      <h2>History — ${esc(spec.item)}</h2>
      ${diffs.length === 0
        ? `<div class="empty-inline">No changes recorded yet.</div>`
        : diffs.join('')}
      <div class="modal-actions">
        <span style="flex:1"></span>
        <button type="button" class="btn-link" data-action="close">Close</button>
      </div>
    </div>`;
  const close = (): void => overlay.remove();
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('[data-action="close"]')!.addEventListener('click', close);
  document.body.append(overlay);
}

function renderRoomColor(c: PaletteColorRow): string {
  return `
    <button class="color-swatch" data-edit-room-color="${esc(c.id)}" title="Edit">
      <span class="swatch" style="background:${esc(c.hex)}"></span>
      <span class="swatch-meta">
        <span class="swatch-name">${esc(c.name)}</span>
        <span class="muted">${esc(c.hex)}${c.code ? ` · ${esc(c.code)}` : ''}</span>
      </span>
    </button>
  `;
}

function openRoomColorModal(opts: { projectId: string; roomId: string; sortOrder: number; color?: PaletteColorRow; onDone: () => void }): void {
  const c = opts.color;
  openModal({
    title: c ? 'Edit color' : 'Add color',
    showDelete: !!c,
    fields: [
      { name: 'name', label: 'Name', required: true, defaultValue: c?.name },
      { name: 'hex', label: 'Hex', type: 'color', defaultValue: c?.hex ?? '#B89A6A' },
      { name: 'vendor', label: 'Vendor', defaultValue: c?.vendor },
      { name: 'code', label: 'Code', defaultValue: c?.code },
    ],
    onSubmit: async (v) => {
      const payload = {
        name: String(v.name),
        hex: String(v.hex).toUpperCase(),
        vendor: emptyToNull(v.vendor),
        code: emptyToNull(v.code),
      };
      if (c) await updatePaletteColor(c.id, payload);
      else await createPaletteColor({ project_id: opts.projectId, room_id: opts.roomId, sort_order: opts.sortOrder, ...payload });
      opts.onDone();
    },
    onDelete: c ? async () => { await deletePaletteColor(c.id); opts.onDone(); } : undefined,
  });
}


/**
 * This room's change orders (0038). One naming only this room adds to it;
 * one covering several rooms, or the whole project, is listed but NOT added —
 * splitting its amount by guesswork would make every room's figure wrong.
 */
function renderRoomChangeOrders(room: RoomRow, changeOrders: ChangeOrderRow[]): string {
  const r = roomChangeOrders(changeOrders, room.id);
  if (r.direct.length === 0 && r.shared.length === 0) return '';
  const item = (co: ChangeOrderRow, shared: boolean): string => `
    <li class="room-co">
      <div>
        <strong>CO #${co.number}</strong> ${esc(co.title)}
        <span class="chip chip-${esc(co.status)}">${esc(CHANGE_ORDER_STATUS_LABEL[co.status])}</span>
        ${shared ? `<div class="muted small">${co.affected_room_ids.length === 0
          ? 'Whole project — not added to this room'
          : 'Shared with other rooms — not added to this room'}</div>` : ''}
      </div>
      <div class="num">${esc(signedMoney(changeOrderTotal(co)))}${co.designer_fee_cents
        ? `<div class="muted small">incl. ${esc(signedMoney(co.designer_fee_cents))} design fee</div>` : ''}</div>
    </li>`;
  return `
    <section class="panel">
      <div class="section-bar">
        <div class="section-bar-left"><h3>Change orders</h3></div>
        ${r.directApprovedCents !== 0
          ? `<span class="muted">Approved for this room: <strong>${esc(signedMoney(r.directApprovedCents))}</strong></span>` : ''}
      </div>
      <ul class="room-co-list">
        ${r.direct.map((co) => item(co, false)).join('')}
        ${r.shared.map((co) => item(co, true)).join('')}
      </ul>
    </section>`;
}
